/**
 * useWalletStateMachine.js — Machine d'état complète du modal EarniWalletModal.
 *
 * Implémente EXACTEMENT la table de transitions §8.4 de la SPEC.
 *
 * Règle de sécurité critique (§8.4 / §9 SPEC) :
 *   `keyMaterial` (keystore + mnemonic) vit UNIQUEMENT dans le state React (useReducer).
 *   Il n'est JAMAIS écrit dans localStorage, sessionStorage, ou dans Redux.
 *   Sur KEY_TIMER_EXPIRED et KEYS_SAVED : remplacé par null dans le state.
 *   Sur RESET : effacé via initialState.
 *
 * Actions exportées (contrat public pour les agents aval) :
 *   OPEN, OPEN_MANAGE, SELECT_EMAIL_LOGIN, SELECT_EMAIL_REGISTER, SELECT_WALLET,
 *   OPEN_KEY_EXPORT, BACK, EMAIL_AUTH_SUCCESS, EMAIL_AUTH_ERROR,
 *   EMAIL_VERIFY_REQUIRED, EMAIL_VERIFIED,
 *   SWITCH_TO_REGISTER, SWITCH_TO_LOGIN,
 *   WALLET_CONNECTED, SIGNATURE_OBTAINED, VERIFY_SUCCESS_EXISTING,
 *   VERIFY_SUCCESS_NEW, USER_REJECTED, NETWORK_MISMATCH, CONNECT_ERROR,
 *   REGISTER_SUCCESS, REGISTER_ERROR, RESET, CLEAR_ERROR,
 *   OTP_SENT, OTP_SEND_ERROR, OTP_VERIFIED, OTP_VERIFY_ERROR,
 *   KEY_TIMER_EXPIRED, KEYS_SAVED
 */
import { useReducer } from 'react';
import { NETWORK_LABEL } from '../config/wagmiConfig';

/* ─── État initial ──────────────────────────────────────────────────────────── */
const initialState = {
  currentState:     'IDLE',
  selectedWalletId: null,
  connectedAddress: null,
  userData:         null,
  /** pendingUser : user en attente de vérification email (état EMAIL_VERIFY) */
  pendingUser:      null,
  error:            null,
  maskedEmail:      null,
  /** registrationToken : preuve SIWE court terme pour /auth/wallet/register (flux REGISTER) */
  registrationToken: null,
  /** keyMaterial vit ici uniquement (React memory, jamais persisté) */
  keyMaterial:      null,
  otpAttempts:      0,
  /** linkMode : true quand le picker est ouvert pour LIER un wallet à un compte
   *  authentifié existant (flux MANAGE → PICKING via OPEN_LINK), au lieu de se
   *  connecter/s'inscrire. Le connect appelle alors POST /auth/wallet/link. */
  linkMode:         false,
  /** resetLinkSent : passe à true après POST /auth/reset-password réussi
   *  (état FORGOT_PASSWORD), bascule l'UI vers le message de confirmation. */
  resetLinkSent:    false,
};

/* ─── Reducer — table de transitions §8.4 ──────────────────────────────────── */
function walletReducer(state, action) {
  switch (action.type) {

    /* ════════════════════════════════════════════════════════════════════════
       GLOBAL : RESET depuis N'IMPORTE QUEL état (§8.4 : * → IDLE)
       Efface TOUT — y compris keyMaterial (effacement sécurisé).
       ════════════════════════════════════════════════════════════════════ */
    case 'RESET':
      return { ...initialState };

    /* ════════════════════════════════════════════════════════════════════════
       CLEAR_ERROR — efface l'erreur sans changer d'état
       (utilisé par clearError() dans le context)
       ════════════════════════════════════════════════════════════════════ */
    case 'CLEAR_ERROR':
      return { ...state, error: null };

    /* ════════════════════════════════════════════════════════════════════════
       IDLE → PICKING
       FROM: IDLE | EVENT: OPEN | TO: PICKING | SIDE EFFECT: clear error + pendingWallet
       ════════════════════════════════════════════════════════════════════ */
    case 'OPEN':
      if (state.currentState !== 'IDLE') return state;
      return {
        ...initialState,
        currentState: 'PICKING',
      };

    /* ════════════════════════════════════════════════════════════════════════
       IDLE → MANAGE (flow gestion wallets authentifiés)
       FROM: IDLE | EVENT: OPEN_MANAGE | TO: MANAGE
       ════════════════════════════════════════════════════════════════════ */
    case 'OPEN_MANAGE':
      if (state.currentState !== 'IDLE') return state;
      return {
        ...initialState,
        currentState: 'MANAGE',
      };

    /* ════════════════════════════════════════════════════════════════════════
       MANAGE → PICKING (mode liaison d'un nouveau wallet au compte courant)
       FROM: MANAGE | EVENT: OPEN_LINK | TO: PICKING | SIDE EFFECT: linkMode=true
       Le connect en linkMode appelle POST /auth/wallet/link (preuve SIWE) au lieu
       de verify-signature. Retour via BACK → MANAGE.
       ════════════════════════════════════════════════════════════════════ */
    case 'OPEN_LINK':
      if (state.currentState !== 'MANAGE') return state;
      return {
        ...initialState,
        currentState: 'PICKING',
        linkMode:     true,
      };

    /* ════════════════════════════════════════════════════════════════════════
       PICKING → EMAIL_LOGIN
       FROM: PICKING | EVENT: SELECT_EMAIL_LOGIN | TO: EMAIL_LOGIN
       ════════════════════════════════════════════════════════════════════ */
    case 'SELECT_EMAIL_LOGIN':
      if (state.currentState !== 'PICKING') return state;
      return { ...state, currentState: 'EMAIL_LOGIN', error: null };

    /* ════════════════════════════════════════════════════════════════════════
       PICKING → EMAIL_REGISTER
       FROM: PICKING | EVENT: SELECT_EMAIL_REGISTER | TO: EMAIL_REGISTER
       ════════════════════════════════════════════════════════════════════ */
    case 'SELECT_EMAIL_REGISTER':
      if (state.currentState !== 'PICKING') return state;
      return { ...state, currentState: 'EMAIL_REGISTER', error: null };

    /* ════════════════════════════════════════════════════════════════════════
       PICKING → WALLET_CONNECTING
       FROM: PICKING | EVENT: SELECT_WALLET(id) | TO: WALLET_CONNECTING
       SIDE EFFECT: stocke selectedWalletId
       ════════════════════════════════════════════════════════════════════ */
    case 'SELECT_WALLET':
      if (state.currentState !== 'PICKING') return state;
      return {
        ...state,
        currentState:     'WALLET_CONNECTING',
        selectedWalletId: action.payload?.walletId ?? null,
        error:            null,
      };

    /* ════════════════════════════════════════════════════════════════════════
       PICKING / MANAGE → KEY_OTP_REQUEST
       FROM: PICKING | EVENT: OPEN_KEY_EXPORT | TO: KEY_OTP_REQUEST
       FROM: MANAGE  | EVENT: OPEN_KEY_EXPORT | TO: KEY_OTP_REQUEST
       SIDE EFFECT: (contexte auth uniquement)
       ════════════════════════════════════════════════════════════════════ */
    case 'OPEN_KEY_EXPORT':
      if (state.currentState !== 'PICKING' && state.currentState !== 'MANAGE') return state;
      return { ...state, currentState: 'KEY_OTP_REQUEST', error: null };

    /* ════════════════════════════════════════════════════════════════════════
       EMAIL_LOGIN / EMAIL_REGISTER → PICKING
       FROM: EMAIL_LOGIN | EVENT: BACK | TO: PICKING | SIDE EFFECT: clear form errors
       FROM: EMAIL_REGISTER | EVENT: BACK | TO: PICKING
       ════════════════════════════════════════════════════════════════════ */
    case 'BACK':
      if (state.currentState === 'PICKING' && state.linkMode) {
        /* PICKING (linkMode) → MANAGE : annule la liaison, retour à la gestion */
        return { ...initialState, currentState: 'MANAGE' };
      }
      if (state.currentState === 'EMAIL_LOGIN' || state.currentState === 'EMAIL_REGISTER') {
        return { ...state, currentState: 'PICKING', error: null };
      }
      if (state.currentState === 'FORGOT_PASSWORD') {
        /* FORGOT_PASSWORD → EMAIL_LOGIN : retour au formulaire de connexion */
        return { ...state, currentState: 'EMAIL_LOGIN', error: null, resetLinkSent: false };
      }
      if (state.currentState === 'EMAIL_VERIFY') {
        /* EMAIL_VERIFY → EMAIL_REGISTER : retour au formulaire d'inscription ; efface pendingUser */
        return { ...state, currentState: 'EMAIL_REGISTER', pendingUser: null, error: null };
      }
      if (state.currentState === 'WALLET_REGISTERING') {
        /* WALLET_REGISTERING → PICKING : déconnecter wagmi (géré par le composant via wagmi disconnect) */
        return { ...state, currentState: 'PICKING', connectedAddress: null, error: null };
      }
      if (state.currentState === 'KEY_OTP_VERIFY') {
        /* KEY_OTP_VERIFY → KEY_OTP_REQUEST : efface la saisie OTP */
        return { ...state, currentState: 'KEY_OTP_REQUEST', error: null, otpAttempts: 0 };
      }
      if (state.currentState === 'KEY_OTP_REQUEST') {
        /* KEY_OTP_REQUEST → MANAGE : retour à la gestion des wallets */
        return { ...state, currentState: 'MANAGE', error: null };
      }
      return state;

    /* ════════════════════════════════════════════════════════════════════════
       EMAIL_REGISTER → EMAIL_VERIFY (vérification email 2FA post-inscription)
       FROM: EMAIL_REGISTER | EVENT: EMAIL_VERIFY_REQUIRED(user) | TO: EMAIL_VERIFY
       SIDE EFFECT: stocke pendingUser (user isVerified=false + accessToken)
       ════════════════════════════════════════════════════════════════════ */
    case 'EMAIL_VERIFY_REQUIRED':
      if (state.currentState !== 'EMAIL_REGISTER') return state;
      return {
        ...state,
        currentState: 'EMAIL_VERIFY',
        pendingUser:  action.payload,
        error:        null,
      };

    /* ════════════════════════════════════════════════════════════════════════
       EMAIL_VERIFY → SUCCESS (code validé, email vérifié)
       FROM: EMAIL_VERIFY | EVENT: EMAIL_VERIFIED(user) | TO: SUCCESS
       SIDE EFFECT: userData = user complet vérifié ; pendingUser effacé
       ════════════════════════════════════════════════════════════════════ */
    case 'EMAIL_VERIFIED':
      if (state.currentState !== 'EMAIL_VERIFY') return state;
      return {
        ...state,
        currentState: 'SUCCESS',
        userData:     action.payload,
        pendingUser:  null,
        error:        null,
      };

    /* ════════════════════════════════════════════════════════════════════════
       EMAIL_LOGIN / EMAIL_REGISTER → SUCCESS
       FROM: * | EVENT: EMAIL_AUTH_SUCCESS(user) | TO: SUCCESS | SIDE EFFECT: userData
       ════════════════════════════════════════════════════════════════════ */
    case 'EMAIL_AUTH_SUCCESS':
      if (state.currentState !== 'EMAIL_LOGIN' && state.currentState !== 'EMAIL_REGISTER') return state;
      return {
        ...state,
        currentState: 'SUCCESS',
        userData:     action.payload,
        error:        null,
      };

    /* ════════════════════════════════════════════════════════════════════════
       EMAIL_LOGIN / EMAIL_REGISTER : erreur inline (pas de changement d'état)
       FROM: EMAIL_LOGIN | EVENT: EMAIL_AUTH_ERROR | TO: EMAIL_LOGIN | SIDE EFFECT: inline error
       FROM: EMAIL_REGISTER | EVENT: EMAIL_AUTH_ERROR | TO: EMAIL_REGISTER
       ════════════════════════════════════════════════════════════════════ */
    case 'EMAIL_AUTH_ERROR':
      if (state.currentState !== 'EMAIL_LOGIN' && state.currentState !== 'EMAIL_REGISTER') return state;
      return {
        ...state,
        error: { message: action.payload?.message ?? 'Authentication error.', recoverable: true },
      };

    /* ════════════════════════════════════════════════════════════════════════
       EMAIL_LOGIN → EMAIL_REGISTER (et inverse)
       FROM: EMAIL_LOGIN  | EVENT: SWITCH_TO_REGISTER | TO: EMAIL_REGISTER
       FROM: EMAIL_REGISTER | EVENT: SWITCH_TO_LOGIN | TO: EMAIL_LOGIN
       ════════════════════════════════════════════════════════════════════ */
    case 'SWITCH_TO_REGISTER':
      if (state.currentState !== 'EMAIL_LOGIN') return state;
      return { ...state, currentState: 'EMAIL_REGISTER', error: null };

    case 'SWITCH_TO_LOGIN':
      if (state.currentState !== 'EMAIL_REGISTER' && state.currentState !== 'FORGOT_PASSWORD') return state;
      return { ...state, currentState: 'EMAIL_LOGIN', error: null, resetLinkSent: false };

    /* ════════════════════════════════════════════════════════════════════════
       EMAIL_LOGIN → FORGOT_PASSWORD (flow mot de passe oublié)
       FROM: EMAIL_LOGIN | EVENT: SELECT_FORGOT_PASSWORD | TO: FORGOT_PASSWORD
       Le panel ForgotPasswordPanel envoie POST /auth/reset-password (safe-enum).
       Sur succès, dispatch RESET_LINK_SENT (reste dans FORGOT_PASSWORD avec resetLinkSent=true).
       Retour via SWITCH_TO_LOGIN.
       ════════════════════════════════════════════════════════════════════ */
    case 'SELECT_FORGOT_PASSWORD':
      if (state.currentState !== 'EMAIL_LOGIN') return state;
      return { ...state, currentState: 'FORGOT_PASSWORD', error: null, resetLinkSent: false };

    case 'RESET_LINK_SENT':
      if (state.currentState !== 'FORGOT_PASSWORD') return state;
      return { ...state, resetLinkSent: true, error: null };

    /* ════════════════════════════════════════════════════════════════════════
       WALLET_CONNECTING : adresse obtenue (reste dans WALLET_CONNECTING)
       FROM: WALLET_CONNECTING | EVENT: WALLET_CONNECTED(address)
       TO: WALLET_CONNECTING | SIDE EFFECT: stocke address, lance nonce + signature
       ════════════════════════════════════════════════════════════════════ */
    case 'WALLET_CONNECTED':
      if (state.currentState !== 'WALLET_CONNECTING') return state;
      return {
        ...state,
        connectedAddress: action.payload?.address ?? null,
        error:            null,
      };

    /* ════════════════════════════════════════════════════════════════════════
       WALLET_CONNECTING : signature obtenue (reste dans WALLET_CONNECTING)
       FROM: WALLET_CONNECTING | EVENT: SIGNATURE_OBTAINED
       TO: WALLET_CONNECTING | SIDE EFFECT: avance le step indicator
       ════════════════════════════════════════════════════════════════════ */
    case 'SIGNATURE_OBTAINED':
      if (state.currentState !== 'WALLET_CONNECTING') return state;
      return state; /* Le composant lit currentState + action pour avancer l'étape */

    /* ════════════════════════════════════════════════════════════════════════
       WALLET_CONNECTING → SUCCESS (wallet existant)
       FROM: WALLET_CONNECTING | EVENT: VERIFY_SUCCESS_EXISTING(user) | TO: SUCCESS
       ════════════════════════════════════════════════════════════════════ */
    case 'VERIFY_SUCCESS_EXISTING':
      if (state.currentState !== 'WALLET_CONNECTING') return state;
      return {
        ...state,
        currentState: 'SUCCESS',
        userData:     action.payload,
        error:        null,
      };

    /* ════════════════════════════════════════════════════════════════════════
       WALLET_CONNECTING → WALLET_REGISTERING (nouvelle adresse)
       FROM: WALLET_CONNECTING | EVENT: VERIFY_SUCCESS_NEW | TO: WALLET_REGISTERING
       SIDE EFFECT: conserve connectedAddress
       ════════════════════════════════════════════════════════════════════ */
    case 'VERIFY_SUCCESS_NEW':
      if (state.currentState !== 'WALLET_CONNECTING') return state;
      return {
        ...state,
        currentState: 'WALLET_REGISTERING',
        connectedAddress: action.payload?.address ?? state.connectedAddress,
        registrationToken: action.payload?.registrationToken ?? null,
        error: null,
      };

    /* ════════════════════════════════════════════════════════════════════════
       WALLET_CONNECTING → MANAGE (wallet lié au compte courant, linkMode)
       FROM: WALLET_CONNECTING | EVENT: WALLET_LINKED | TO: MANAGE
       SIDE EFFECT: reset linkMode ; le switcher recharge la liste au re-mount.
       ════════════════════════════════════════════════════════════════════ */
    case 'WALLET_LINKED':
      if (state.currentState !== 'WALLET_CONNECTING') return state;
      return {
        ...initialState,
        currentState: 'MANAGE',
      };

    /* ════════════════════════════════════════════════════════════════════════
       WALLET_CONNECTING → ERROR (refus utilisateur)
       FROM: WALLET_CONNECTING | EVENT: USER_REJECTED | TO: ERROR
       SIDE EFFECT: error.recoverable = true
       ════════════════════════════════════════════════════════════════════ */
    case 'USER_REJECTED':
      if (state.currentState !== 'WALLET_CONNECTING') return state;
      return {
        ...state,
        currentState: 'ERROR',
        error: {
          message:    'The connection was rejected in the wallet.',
          code:       'USER_REJECTED',
          recoverable: true,
        },
      };

    /* ════════════════════════════════════════════════════════════════════════
       WALLET_CONNECTING → ERROR (mauvais réseau)
       FROM: WALLET_CONNECTING | EVENT: NETWORK_MISMATCH | TO: ERROR
       SIDE EFFECT: error.code='NETWORK_MISMATCH', recoverable=true
       ════════════════════════════════════════════════════════════════════ */
    case 'NETWORK_MISMATCH':
      if (state.currentState !== 'WALLET_CONNECTING') return state;
      return {
        ...state,
        currentState: 'ERROR',
        error: {
          message:    `Wrong network detected. Please switch to ${NETWORK_LABEL}.`,
          code:       'NETWORK_MISMATCH',
          recoverable: true,
        },
      };

    /* ════════════════════════════════════════════════════════════════════════
       WALLET_CONNECTING → ERROR (erreur générique)
       FROM: WALLET_CONNECTING | EVENT: CONNECT_ERROR(err) | TO: ERROR
       SIDE EFFECT: recoverable=true
       ════════════════════════════════════════════════════════════════════ */
    case 'CONNECT_ERROR':
      if (state.currentState !== 'WALLET_CONNECTING') return state;
      return {
        ...state,
        currentState: 'ERROR',
        error: {
          message:    action.payload?.message ?? 'Wallet connection error.',
          recoverable: true,
        },
      };

    /* ════════════════════════════════════════════════════════════════════════
       WALLET_REGISTERING → SUCCESS
       FROM: WALLET_REGISTERING | EVENT: REGISTER_SUCCESS(user) | TO: SUCCESS
       ════════════════════════════════════════════════════════════════════ */
    case 'REGISTER_SUCCESS':
      if (state.currentState !== 'WALLET_REGISTERING') return state;
      return {
        ...state,
        currentState: 'SUCCESS',
        userData:     action.payload,
        error:        null,
      };

    /* ════════════════════════════════════════════════════════════════════════
       WALLET_REGISTERING : erreur inline (pas de changement d'état)
       FROM: WALLET_REGISTERING | EVENT: REGISTER_ERROR | TO: WALLET_REGISTERING
       ════════════════════════════════════════════════════════════════════ */
    case 'REGISTER_ERROR':
      if (state.currentState !== 'WALLET_REGISTERING') return state;
      return {
        ...state,
        error: {
          message:    action.payload?.message ?? 'Registration error.',
          recoverable: true,
        },
      };

    /* ════════════════════════════════════════════════════════════════════════
       ERROR → PICKING (retry)
       FROM: ERROR | EVENT: RETRY | TO: PICKING | SIDE EFFECT: clear error
       ════════════════════════════════════════════════════════════════════ */
    case 'RETRY':
      if (state.currentState !== 'ERROR') return state;
      return { ...state, currentState: 'PICKING', error: null };

    /* ════════════════════════════════════════════════════════════════════════
       KEY_OTP_REQUEST → KEY_OTP_VERIFY
       FROM: KEY_OTP_REQUEST | EVENT: OTP_SENT(maskedEmail) | TO: KEY_OTP_VERIFY
       ════════════════════════════════════════════════════════════════════ */
    case 'OTP_SENT':
      if (state.currentState !== 'KEY_OTP_REQUEST') return state;
      return {
        ...state,
        currentState: 'KEY_OTP_VERIFY',
        maskedEmail:  action.payload?.maskedEmail ?? null,
        error:        null,
        otpAttempts:  0,
      };

    /* ════════════════════════════════════════════════════════════════════════
       KEY_OTP_REQUEST : erreur inline (pas de changement d'état)
       FROM: KEY_OTP_REQUEST | EVENT: OTP_SEND_ERROR | TO: KEY_OTP_REQUEST
       ════════════════════════════════════════════════════════════════════ */
    case 'OTP_SEND_ERROR':
      if (state.currentState !== 'KEY_OTP_REQUEST') return state;
      return {
        ...state,
        error: {
          message:    action.payload?.message ?? 'Failed to send the code.',
          recoverable: true,
        },
      };

    /* ════════════════════════════════════════════════════════════════════════
       KEY_OTP_VERIFY → KEY_DISPLAY
       FROM: KEY_OTP_VERIFY | EVENT: OTP_VERIFIED(keyMaterial) | TO: KEY_DISPLAY
       SIDE EFFECT: keyMaterial stocké UNIQUEMENT ici (React memory, §8.9)
       ════════════════════════════════════════════════════════════════════ */
    case 'OTP_VERIFIED':
      if (state.currentState !== 'KEY_OTP_VERIFY') return state;
      return {
        ...state,
        currentState: 'KEY_DISPLAY',
        keyMaterial:  action.payload,  /* keystore + mnemonic + address */
        error:        null,
        otpAttempts:  0,
      };

    /* ════════════════════════════════════════════════════════════════════════
       KEY_OTP_VERIFY : erreur inline + compteur de tentatives
       FROM: KEY_OTP_VERIFY | EVENT: OTP_VERIFY_ERROR | TO: KEY_OTP_VERIFY
       SIDE EFFECT: increment otpAttempts. Si otpAttempts ≥ 5 → RESET (lockout)
       ════════════════════════════════════════════════════════════════════ */
    case 'OTP_VERIFY_ERROR': {
      if (state.currentState !== 'KEY_OTP_VERIFY') return state;
      const attempts = (state.otpAttempts ?? 0) + 1;
      if (attempts >= 5) {
        /* Lockout après 5 tentatives → RESET complet */
        return { ...initialState };
      }
      return {
        ...state,
        error: {
          message:    action.payload?.message ?? 'Invalid OTP code.',
          recoverable: true,
        },
        otpAttempts: attempts,
      };
    }

    /* ════════════════════════════════════════════════════════════════════════
       KEY_DISPLAY → IDLE : timer expiré
       FROM: KEY_DISPLAY | EVENT: KEY_TIMER_EXPIRED | TO: IDLE
       SIDE EFFECT: keyMaterial remplacé par null (effacement sécurisé)
       ════════════════════════════════════════════════════════════════════ */
    case 'KEY_TIMER_EXPIRED':
      if (state.currentState !== 'KEY_DISPLAY') return state;
      return {
        ...initialState,
        /* keyMaterial = null grâce à initialState */
      };

    /* ════════════════════════════════════════════════════════════════════════
       KEY_DISPLAY → IDLE : clés sauvegardées
       FROM: KEY_DISPLAY | EVENT: KEYS_SAVED | TO: IDLE
       SIDE EFFECT: keyMaterial remplacé par null (effacement sécurisé)
       ════════════════════════════════════════════════════════════════════ */
    case 'KEYS_SAVED':
      if (state.currentState !== 'KEY_DISPLAY') return state;
      return { ...initialState };

    /* ════════════════════════════════════════════════════════════════════════
       __PREVIEW_HYDRATE__ — PREVIEW UIX UNIQUEMENT : force un état + contexte
       (currentState, pendingUser, keyMaterial, userData, …) pour capturer chaque
       panel en snapshot. Jamais déclenché en runtime app (gate via prop previewState
       du modal, non câblée dans les consommateurs réels). Aucun impact prod.
       ════════════════════════════════════════════════════════════════════ */
    case '__PREVIEW_HYDRATE__':
      return { ...state, ...(action.payload || {}) };

    default:
      return state;
  }
}

/**
 * useWalletStateMachine — hook exposant le state et le dispatch typé.
 *
 * @returns {{ state: import('../types').StateMachineState, dispatch: function }}
 */
export function useWalletStateMachine() {
  const [state, dispatch] = useReducer(walletReducer, initialState);
  return { state, dispatch };
}

export default useWalletStateMachine;

/**
 * ACTION_TYPES — export des noms d'action pour référence externe (agents aval).
 * Permet d'éviter les magic strings dans les panels et hooks.
 */
export const ACTION_TYPES = {
  OPEN:                    'OPEN',
  OPEN_MANAGE:             'OPEN_MANAGE',
  OPEN_LINK:               'OPEN_LINK',
  WALLET_LINKED:           'WALLET_LINKED',
  SELECT_EMAIL_LOGIN:      'SELECT_EMAIL_LOGIN',
  SELECT_EMAIL_REGISTER:   'SELECT_EMAIL_REGISTER',
  SELECT_WALLET:           'SELECT_WALLET',
  OPEN_KEY_EXPORT:         'OPEN_KEY_EXPORT',
  BACK:                    'BACK',
  EMAIL_AUTH_SUCCESS:      'EMAIL_AUTH_SUCCESS',
  EMAIL_AUTH_ERROR:        'EMAIL_AUTH_ERROR',
  EMAIL_VERIFY_REQUIRED:   'EMAIL_VERIFY_REQUIRED',
  EMAIL_VERIFIED:          'EMAIL_VERIFIED',
  SWITCH_TO_REGISTER:      'SWITCH_TO_REGISTER',
  SWITCH_TO_LOGIN:         'SWITCH_TO_LOGIN',
  SELECT_FORGOT_PASSWORD:  'SELECT_FORGOT_PASSWORD',
  RESET_LINK_SENT:         'RESET_LINK_SENT',
  WALLET_CONNECTED:        'WALLET_CONNECTED',
  SIGNATURE_OBTAINED:      'SIGNATURE_OBTAINED',
  VERIFY_SUCCESS_EXISTING: 'VERIFY_SUCCESS_EXISTING',
  VERIFY_SUCCESS_NEW:      'VERIFY_SUCCESS_NEW',
  USER_REJECTED:           'USER_REJECTED',
  NETWORK_MISMATCH:        'NETWORK_MISMATCH',
  CONNECT_ERROR:           'CONNECT_ERROR',
  REGISTER_SUCCESS:        'REGISTER_SUCCESS',
  REGISTER_ERROR:          'REGISTER_ERROR',
  RESET:                   'RESET',
  CLEAR_ERROR:             'CLEAR_ERROR',
  OTP_SENT:                'OTP_SENT',
  OTP_SEND_ERROR:          'OTP_SEND_ERROR',
  OTP_VERIFIED:            'OTP_VERIFIED',
  OTP_VERIFY_ERROR:        'OTP_VERIFY_ERROR',
  KEY_TIMER_EXPIRED:       'KEY_TIMER_EXPIRED',
  KEYS_SAVED:              'KEYS_SAVED',
  RETRY:                   'RETRY',
};
